import { AvailablePlanType } from "@common-shared/config/coverage.config"
import {
    FeatureFlag,
    FeatureFlagsExistenceLogicalOperator,
} from "@common-shared/config/featureFlags.config"
import { COMMON_ONE_VERIFY_CHECKER_CONFIG } from "@common-shared/config/oneVerify/oneVerifyChecker.config"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import {
    PoliciesListType,
    PolicyModalityCode,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import { IOVCoverageResultDemographics } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import type {
    ICheckerRunningTypeTab,
    ICheckerTypeTab,
} from "../types/objects.types"
import { FEATURE_FLAGS_CONFIG } from "./featureFlags.config"
import { GENERAL_CONFIG } from "./general.config"

import aetnaIcon from "@front-shared/assets/images/payers/aetna.svg"
import cignaIcon from "@front-shared/assets/images/payers/cigna.svg"
import humanaIcon from "@front-shared/assets/images/payers/humana.svg"
import tuftsIcon from "@front-shared/assets/images/payers/tufts.svg"

export enum CoverageCheckerMode {
    MANUAL = "manual",
    CSV = "csv",
}

export enum CoverageCheckerRunningState {
    INPUT_MANUAL = "INPUT_MANUAL",
    INPUT_CSV = "INPUT_CSV",
    PROCESS_CSV = "PROCESS_CSV",
    PREVIEW_CSV = "PREVIEW_CSV",
    RESULTS_MANUAL = "RESULTS_MANUAL",
    RESULTS_CSV = "RESULTS_CSV",
}

export const CHECKER_CONFIG = {
    ...COMMON_ONE_VERIFY_CHECKER_CONFIG,

    maxGetEstimateRetry: 3,

    // How many checks we process at once
    processBatchSize: 10,

    // Upper bound on bulk throughput for every user: at most
    // maxBulkChecksPerWindow checks dispatched in any bulkChecksWindowMs span,
    // so fast payer responses can't burst the coverage API. Defaults to 40 per
    // minute. Set either value to 0 to disable pacing. Keep the limit a multiple
    // of processBatchSize so capacity is always granted a whole batch at a time.
    maxBulkChecksPerWindow: 40,
    bulkChecksWindowMs: 60 * 1000,

    // Padding added to each pacing sleep to absorb timer inaccuracy. Browsers
    // can fire a timer marginally early, leaving Date.now() just short of the
    // cutoff and costing another pass on a sub-millisecond residual wait.
    bulkChecksTimerSlopMs: 50,

    get inNetworkOnlyCheckTypes(): QuotaUsageType[] {
        return [
            QuotaUsageType.MEDICAID,
            QuotaUsageType.AGENTIC_PORTAL,
            QuotaUsageType.AGENTIC_PHONE,
        ]
    },

    get checkerRunningTypeTabs(): ICheckerRunningTypeTab[] {
        return [
            {
                key: CoverageCheckerMode.MANUAL,
                label: "Single Patient",
                featureFlag: FEATURE_FLAGS_CONFIG.manualCheckerFeatureFlags,
                featureFlagExistenceOperator:
                    FeatureFlagsExistenceLogicalOperator.OR,
            },
            {
                key: CoverageCheckerMode.CSV,
                label: "Multiple Patients",
                featureFlag: FEATURE_FLAGS_CONFIG.bulkCheckerFeatureFlags,
                featureFlagExistenceOperator:
                    FeatureFlagsExistenceLogicalOperator.OR,
            },
        ]
    },

    get manualTypeTabs(): ICheckerTypeTab[] {
        return [
            {
                key: QuotaUsageType.GENERAL,
                label: "Standard",
                featureFlag: FeatureFlag.ONE_VERIFY_STANDARD_MANUAL_CHECK,
            },
            {
                key: QuotaUsageType.DISCOVER,
                label: "Discover",
                featureFlag: FeatureFlag.ONE_VERIFY_DISCOVER_MANUAL_CHECK,
            },
            {
                key: QuotaUsageType.SMART_SCAN,
                label: "Smart Scan",
                featureFlag: FeatureFlag.ONE_VERIFY_SCAN_MANUAL_CHECK,
            },
            {
                key: QuotaUsageType.MEDICAID,
                label: "Medicaid",
                featureFlag: FeatureFlag.ONE_VERIFY_MEDICAID_MANUAL_CHECK,
            },
            {
                key: QuotaUsageType.AGENTIC_PHONE,
                label: "Agentic",
                featureFlag: FeatureFlag.ONE_VERIFY_AGENTIC_PHONE_MANUAL_CHECK,
                limitedToModalityCodes: [
                    PolicyModalityCode.IOP,
                    PolicyModalityCode.PHP,
                ],
            },
        ]
    },

    get demographicsDifference(): Record<
        "inputRequiredKeys" | "ignoreKeys",
        (keyof IOVCoverageResultDemographics)[]
    > {
        return {
            inputRequiredKeys: ["memberId"],
            ignoreKeys: ["gender", "address"],
        }
    },

    get planTypeMappingByViewType(): Record<
        PoliciesListType,
        Partial<Record<AvailablePlanType, string>>
    > {
        return {
            [PoliciesListType.MEDICAID]: {
                [AvailablePlanType.ORIGINAL_MEDICAID]:
                    CHECKER_CONFIG.planTypeMapping[
                        AvailablePlanType.ORIGINAL_MEDICAID
                    ],
                [AvailablePlanType.MANAGED_MEDICAID]:
                    CHECKER_CONFIG.planTypeMapping[
                        AvailablePlanType.MANAGED_MEDICAID
                    ],
                [AvailablePlanType.NOT_APPLICABLE]:
                    CHECKER_CONFIG.planTypeMapping[
                        AvailablePlanType.NOT_APPLICABLE
                    ],
            },
            [PoliciesListType.HISTORY_SEARCH]: this.planTypeMapping,
            [PoliciesListType.OVERRIDES]: this.planTypeMapping,
            [PoliciesListType.PLAN_YEAR_RESETS]: this.planTypeMapping,
            [PoliciesListType.REPORTS]: this.planTypeMapping,
            [PoliciesListType.AGENTIC]: this.planTypeMapping,
            [PoliciesListType.ASSIGNED_TASKS]: this.planTypeMapping,
            [PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS]:
                this.planTypeMapping,
            [PoliciesListType.TASK_ASSIGNMENTS]: this.planTypeMapping,
            [PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS]:
                this.planTypeMapping,
            [PoliciesListType.ASSIGNED_TASKS_MEDICAID]: this.planTypeMapping,
            [PoliciesListType.TASK_ASSIGNMENTS_MEDICAID]: this.planTypeMapping,
        }
    },

    get supportedPayersUrl(): Record<QuotaUsageType, string | null> {
        return {
            [QuotaUsageType.GENERAL]: GENERAL_CONFIG.supportedInsurersUrl,
            [QuotaUsageType.DISCOVER]:
                GENERAL_CONFIG.supportedInsurersDiscoverUrl,
            [QuotaUsageType.SMART_SCAN]: null,
            [QuotaUsageType.MEDICAID]: null,
            [QuotaUsageType.AGENTIC_PORTAL]:
                GENERAL_CONFIG.supportedInsurersAgenticUrl,
            [QuotaUsageType.AGENTIC_PHONE]:
                GENERAL_CONFIG.supportedInsurersAgenticUrl,
        }
    },

    get payerIconByExternalId(): Record<string, string> {
        return {
            "60054": aetnaIcon,
            "62308": cignaIcon,
            HUMANA: humanaIcon,
            "04298": tuftsIcon,
        }
    },
}
